import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Typography,
  Radio,
} from "antd";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ModelSelectOption from "@/components/Elements/ModelSelectOption";
import VersionEdit from "@/components/InterventionTemplate/VersionEdit";
import { ProgressEditField } from "@/components/ProgressEdit";
import { useGetCurrentUserOrganization } from "@/entities/current-user";
import { INTERVENTION_TEMPLATES_FILTERS } from "@/entities/intervention-templates";
import { useInterventionType } from "@/entities/referential/referential-intervention-types";
import { useOnFormClose, useGenericFormErrorNotification } from "@/hooks/forms";
import { useChain } from "@/hooks/useChain";
import AssetsSelectParent from "@/modules/assets/assets-select-parent";
import { useFormValidation } from "@/modules/form-validation";
import {
  InterventionTemplateSelect,
  getInterventionTemplateSelectFieldValue,
} from "@/modules/intervention-template/intervention-template-select";
import LocationSelectParent from "@/modules/location/location-select-parent";
import OrganizationSelect from "@/modules/organization/organization-select";
import QueryableSelect from "@/modules/queryable-select";
import {
  getSelectSingleValue,
  getSelectMultipleValues,
} from "@/modules/select";
import { SidePanelFooter } from "@/modules/side-panel";
import { TranslateCostSymbol } from "@/modules/translate";
import UsersSelect from "@/modules/users/users-select";
import { InputNumber } from "@/ui/input-number";
import { toDateString } from "@/utilities/date";

// TODO DEV-8551 `Option` is deprecated. Please use `options` instead.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const { Option } = Select;
const { useForm, useWatch } = Form;
const { Title } = Typography;

// TODO IMP-1214 fix complexity
/* eslint-disable complexity */
const InterventionForm = ({
  model,
  mutation,
  saveButtonLabel,
  initialValues,
  defaultTemplate,
  onMutate,
}) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const selectedAsset = useWatch("asset", form);
  const assetId = model?.asset_id ?? selectedAsset?.value;

  const [activeDefaultTemplate, setActiveDefaultTemplate] =
    useState(defaultTemplate);

  const selectedInterventionType = useWatch("interventionType", form);
  const interventionTypeId =
    model?.intervention_type_id ??
    selectedInterventionType ??
    activeDefaultTemplate?.intervention_type_id;

  const isInEditMode = model && model?.id;
  const modelHasTemplate = model?.intervention_template_id ? true : false;
  const userCanSelectTemplate = assetId && interventionTypeId ? true : false;

  const [selectedOrganization, setSelectedOrganization] = useState();
  const [selectedLocation, setSelectedLocation] = useState();

  const [templateFilters, setTemplateFilters] = useState({
    [INTERVENTION_TEMPLATES_FILTERS.ASSOCIABLE]: assetId ?? 0,
    [INTERVENTION_TEMPLATES_FILTERS.INTERVENTION_TYPE_ID]:
      model?.intervention_type_id,
  });

  const templateSelectGroups = [
    {
      // context = "local" => template is linked to the asset by the organization and/or by the asset type
      label: t("intervention:form.templateSelectGroup.local"),
      filterFn: (template) =>
        template.context === "local" ||
        template.id === activeDefaultTemplate?.id,
    },
    {
      // context = "global" => template is not linked to any organization or asset type
      label: t("intervention:form.templateSelectGroup.global"),
      filterFn: (template) =>
        template.context !== "local" &&
        template.id !== activeDefaultTemplate?.id,
    },
  ];

  const { types, subTypes } = useInterventionType();
  const selectedSubTypes = subTypes(interventionTypeId);
  const { isLoading, organization = {} } = useGetCurrentUserOrganization();

  const setInitialValues = (initialValues) => {
    setSelectedOrganization(initialValues?.organization?.value);
    setSelectedLocation(initialValues?.location?.value);
  };

  useEffect(() => {
    setTemplateFilters((currentState) => ({
      ...currentState,
      [INTERVENTION_TEMPLATES_FILTERS.ASSOCIABLE]: assetId ?? 0,
    }));
  }, [assetId]);

  useEffect(() => {
    if (initialValues) setInitialValues(initialValues);
  }, []);

  const handleAssetSelected = (_option, assetData) => {
    const defaultTemplate = assetData?.default_template;
    setActiveDefaultTemplate(defaultTemplate);

    if (defaultTemplate) {
      form.setFieldsValue({
        interventionType: defaultTemplate.intervention_type_id,
        template: getInterventionTemplateSelectFieldValue(defaultTemplate),
      });
      setTemplateFilters((currentState) => ({
        ...currentState,
        [INTERVENTION_TEMPLATES_FILTERS.INTERVENTION_TYPE_ID]:
          defaultTemplate.intervention_type_id,
      }));
    } else {
      form.setFieldsValue({ template: undefined });
    }
  };

  const handleOrgChange = (org) => {
    setSelectedOrganization(org);
    setSelectedLocation(undefined);
    form.setFieldsValue({ location: undefined, asset: undefined });
  };

  const handleLocChange = (loc) => {
    setSelectedLocation(loc);
    form.setFieldsValue({ asset: undefined });
  };

  const handleTemplateSelected = (_option, templateData) => {
    if (templateData?.name) {
      form.setFieldsValue({ name: templateData.name });
    }
  };

  const onClose = useOnFormClose(form);
  const onMutateSuccess = useChain([
    () =>
      queryClient.invalidateQueries({
        queryKey: ["recommendations", "list"],
      }),
    onClose,
    onMutate,
  ]);

  useGenericFormErrorNotification(mutation.isError);
  const { validationErrors, validation } = useFormValidation(
    mutation.isError,
    mutation.error,
    [
      "name",
      "description",
      "application_date",
      "scheduled_date",
      "intervention_template_id",
      "users",
      "organization_id",
      "location_id",
      "asset_id",
      "cost",
      "cost_type",
      "min_days_delay",
      "max_days_delay",
      "recommendation_id",
      "project_id",
      "progress",
      "intervention_type_id",
      "intervention_sub_type_id",
    ]
  );

  const onSave = () => {
    const data = {
      name: form.getFieldValue("name"),
      description: form.getFieldValue("description"),
      application_date: form.getFieldValue("application_date"),
      scheduled_date: toDateString(form.getFieldValue("scheduled_date")),
      provider: form.getFieldValue("provider"),
      users: getSelectMultipleValues(form, "users"),
      asset_id: getSelectSingleValue(form, "asset"),
      cost: form.getFieldValue("cost"),
      cost_type: form.getFieldValue("cost_type"),
      min_days_delay: form.getFieldValue("min_days_delay"),
      max_days_delay: form.getFieldValue("max_days_delay"),
      progress: form.getFieldValue("progress"),
      intervention_type_id: form.getFieldValue("interventionType"),
      intervention_sub_type_id: form.getFieldValue("sub_type"),
    };

    const templateField = form.getFieldValue("template");
    data["intervention_template_id"] = templateField?.value ?? templateField;

    if (!isInEditMode) {
      data["recommendation_id"] = getSelectSingleValue(form, "recommendation");
      data["project_id"] = getSelectSingleValue(form, "project");
    }

    mutation.mutate(data, { onSuccess: onMutateSuccess });
  };

  const onTypeChange = (value) => {
    setTemplateFilters((currentState) => ({
      ...currentState,
      [INTERVENTION_TEMPLATES_FILTERS.INTERVENTION_TYPE_ID]: value,
    }));
    form.resetFields(["sub_type"]);
    form.setFieldsValue({ template: undefined });
  };

  return (
    <Form
      id="interventionForm"
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onFinish={onSave}
      role="form"
    >
      {!isInEditMode && (
        <>
          <Title level={3}>
            {t("intervention:form.associateInterventionWith")}
          </Title>
          <OrganizationSelect
            onChange={handleOrgChange}
            {...validation("organization_id")}
          />
          <LocationSelectParent
            selectedOrganization={selectedOrganization}
            onChange={handleLocChange}
            rules={[{ required: true }]}
            {...validation("location_id")}
          />
          <AssetsSelectParent
            selectedLocation={selectedLocation}
            rules={[{ required: true }]}
            onOptionSelected={handleAssetSelected}
            {...validation("asset_id")}
          />
          <QueryableSelect
            model="projects"
            filterBy="name"
            fields={["id", "name"]}
            dataTestId="project"
            placeholder={t("global:project")}
            name="project"
            label={t("global:project")}
            disabled={!selectedLocation}
            optionFormat={ModelSelectOption}
            additionalFilters={
              selectedLocation && {
                ["filter[location_id]"]: selectedLocation,
              }
            }
            {...validation("project_id")}
          />
          {initialValues?.recommendation?.value && (
            <QueryableSelect
              model="recommendations"
              filterBy="name"
              dataTestId="recommendation"
              placeholder={t("global:recommendation")}
              name="recommendation"
              label={t("global:recommendation")}
              optionFormat={ModelSelectOption}
              disabled={true}
              {...validation("recommendation_id")}
            />
          )}
          <Divider />
        </>
      )}
      <Title level={3}>{t("intervention:form.informations")}</Title>
      <Form.Item
        name="interventionType"
        label={t("intervention:form.type.label")}
        rules={[{ required: true }]}
        {...validation("intervention_type_id")}
      >
        <Select
          placeholder={t("intervention:form.type.label")}
          data-testid="type"
          onChange={onTypeChange}
          disabled={isInEditMode}
        >
          {types.map(({ id, name }) => (
            <Option key={id} value={id}>
              {name}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="sub_type"
        label={t("intervention:form.subtype.label")}
        {...validation("intervention_sub_type_id")}
      >
        <Select
          placeholder={t("intervention:form.subtype.label")}
          data-testid="sub_type"
          disabled={selectedSubTypes.length === 0}
        >
          {selectedSubTypes.map(({ id, name }) => (
            <Option key={id} value={id}>
              {name}
            </Option>
          ))}
        </Select>
      </Form.Item>
      {modelHasTemplate ? (
        <VersionEdit
          name="template"
          label={t("intervention:form.template.label")}
          interventionTemplateId={model?.intervention_template_id}
          {...validation("intervention_template_id")}
        />
      ) : (
        <InterventionTemplateSelect
          name="template"
          label={t("intervention:form.template.label")}
          placeholder={t("intervention:form.template.label")}
          additionalFilters={templateFilters}
          disabled={!userCanSelectTemplate}
          onOptionSelected={handleTemplateSelected}
          groups={templateSelectGroups}
          groupEmptyText={t("intervention:form.noTemplate")}
          dropdownRenderOptions={{ multilineOptions: 2 }}
          styles={{ popup: { root: { minWidth: 500 } } }}
          {...validation("intervention_template_id")}
        />
      )}
      <Form.Item
        name="name"
        label={t("intervention:form.name.label")}
        rules={[{ required: true }]}
        {...validation("name")}
      >
        <Input
          placeholder={t("intervention:form.name.label")}
          data-testid="name"
        />
      </Form.Item>
      <Form.Item
        name="application_date"
        label={t("intervention:form.creationDate.label")}
        tooltip={t("intervention:form.creationDate.tooltip")}
        rules={[{ required: true }]}
        {...validation("application_date")}
      >
        <DatePicker data-testid="application_date" />
      </Form.Item>
      <Form.Item
        name="scheduled_date"
        label={t("intervention:form.scheduledDate.label")}
        {...validation("scheduled_date")}
      >
        <DatePicker data-testid="scheduled_date" />
      </Form.Item>
      <Form.Item
        name="max_days_delay"
        label={t("intervention:form.maxDaysDelay.label")}
        rules={[
          {
            min: 1,
            type: "number",
            message: t("intervention:form.maxDaysDelay.error"),
          },
        ]}
        {...validation("max_days_delay")}
      >
        <InputNumber
          placeholder={t("intervention:form.maxDaysDelay.label")}
          data-testid="max_days_delay"
          min={0}
        />
      </Form.Item>
      <Form.Item
        name="description"
        label={t("intervention:form.description.label")}
        {...validation("description")}
      >
        <Input
          placeholder={t("intervention:form.description.label")}
          data-testid="description"
        />
      </Form.Item>
      <Spin spinning={isLoading}>
        {!isLoading && (
          <Form.Item
            name="provider"
            label={t("intervention:form.provider.label")}
            initialValue={model?.provider ?? organization.name}
            {...validation("provider")}
          >
            <Input
              placeholder={t("intervention:form.provider.label")}
              data-testid="provider"
            />
          </Form.Item>
        )}
      </Spin>
      <UsersSelect
        label={t("intervention:form.users.label")}
        placeholder={t("intervention:form.users.label")}
        {...validation("users")}
      />
      <Form.Item
        name="cost"
        label={t("intervention:form.cost.label")}
        rules={[
          {
            min: 1,
            type: "number",
            message: t("intervention:form.cost.error"),
          },
        ]}
        {...validation("cost")}
      >
        <InputNumber
          placeholder={t("intervention:form.cost.label")}
          data-testid="cost"
          min={0}
          addonAfter={<TranslateCostSymbol />}
        />
      </Form.Item>
      <Form.Item
        name="cost_type"
        label={t("intervention:form.costType.label")}
        {...validation("cost_type")}
      >
        <Radio.Group buttonStyle="solid" data-testid="cost_type">
          {["capex", "opex"].map((costType) => (
            <Radio.Button key={costType} value={costType}>
              {t(`intervention:form.costType.${costType}`)}
            </Radio.Button>
          ))}
        </Radio.Group>
      </Form.Item>
      <ProgressEditField
        label={t("intervention:form.progress.label")}
        alwaysShowHandle
      />
      {validationErrors().others && <Form.Item {...validation("others")} />}
      <SidePanelFooter>
        <Space>
          <Button onClick={onClose}>{t("global:cancel")}</Button>
          <Button
            loading={mutation.isPending}
            type="primary"
            htmlType="submit"
            form="interventionForm"
          >
            {saveButtonLabel}
          </Button>
        </Space>
      </SidePanelFooter>
    </Form>
  );
};

const modelShape = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  reference: PropTypes.string,
  description: PropTypes.string,
  start_date: PropTypes.string,
  due_date: PropTypes.string,
  total_cost: PropTypes.number,
  provider: PropTypes.string,
  location_id: PropTypes.number,
  asset_id: PropTypes.number,
  intervention_template_id: PropTypes.number,
  intervention_type_id: PropTypes.number,
  intervention_sub_type_id: PropTypes.number,
});

const initialValuesShape = PropTypes.shape({
  name: PropTypes.string,
  reference: PropTypes.string,
  description: PropTypes.string,
  start_date: PropTypes.object,
  due_date: PropTypes.object,
  total_cost: PropTypes.number,
  location: PropTypes.object,
  organization: PropTypes.object,
  recommendation: PropTypes.object,
});

InterventionForm.propTypes = {
  model: modelShape,
  mutation: PropTypes.object.isRequired,
  saveButtonLabel: PropTypes.string.isRequired,
  initialValues: initialValuesShape.isRequired,
  defaultTemplate: PropTypes.object,
  onMutate: PropTypes.func,
};

export default InterventionForm;
